import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { aiCallsApi } from '@/api/endpoints'
import {
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Pagination,
  Skeleton,
  SkeletonRows,
  Toolbar,
} from '@/components/common/Ui'
import { dateTime, percent, titleCase } from '@/lib/format'

const PAGE_SIZE = 50

// The windows an operator asks in. 'Today' answers 'is something running
// away with the credit right now'; 'all time' answers 'what has this cost
// us'. The two in between are how you tell one from the other.
const PERIODS = {
  today: 'Today',
  '7d': '7 days',
  '30d': '30 days',
  all: 'All time',
}

const OPERATION_LABELS = {
  ocr: '📄 Image → text',
  stt: '🎤 Audio → text',
  extraction: '🧩 Text → lines',
  // Not part of reading an order: a catalog name being offered in Tamil.
  translation: '🔤 Name → Tamil',
}

const PROVIDER_LABELS = {
  sarvam: 'Sarvam',
  openrouter: 'OpenRouter',
}

const providerName = (code) => PROVIDER_LABELS[code] ?? titleCase(code ?? '')

/**
 * Spend per provider for a period.
 *
 * Grouped by who was paid, not by currency. Two figures labelled 'Spent'
 * side by side — one in rupees, one in dollars — left the reader to work out
 * from the symbol which service each belonged to, and that only happens to
 * work while no two providers bill in the same currency.
 */
function spendByProvider(summary) {
  const totals = new Map()
  for (const row of summary.by_provider) {
    if (!row.currency || !row.cost) continue
    const key = `${row.provider}|${row.currency}`
    const seen = totals.get(key)
    if (seen) seen.cost += row.cost
    else totals.set(key, { provider: row.provider, currency: row.currency, cost: row.cost })
  }
  return [...totals.values()]
}

/**
 * Where a provider's icon lives.
 *
 * The apex domain, not the host we link to. Sarvam's console sits at
 * `dashboard.sarvam.ai` and that subdomain answers /favicon.ico with a 404
 * HTML page — the icon is on `sarvam.ai`. Taking the last two labels gets
 * both providers right; a provider on a two-part TLD would need more, and
 * none of ours is.
 */
function iconHost(dashboard) {
  const host = String(dashboard ?? '').split('/')[0]
  const labels = host.split('.')
  return labels.length > 2 ? labels.slice(-2).join('.') : host
}

/** Costs are never converted between currencies — see the stats endpoint. */
function formatCost(cost, currency) {
  if (cost == null) return '—'
  const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : ''
  // Six places, which is what the cost column stores. Four rounded a real
  // $0.000984 to '$0.001' — a figure that cannot be reconciled against the
  // provider's own log, which is the one thing this number is for. Trailing
  // zeros are still dropped, so a quarter of a rupee is '₹0.25'.
  const amount = Number(cost.toFixed(6)).toString()
  return `${symbol}${amount}${symbol ? '' : ` ${currency ?? ''}`}`
}

export default function AiCalls() {
  const [filters, setFilters] = useState({
    operation: '',
    provider: '',
    status: '',
  })
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState(null)
  const [period, setPeriod] = useState('today')

  const params = { page, page_size: PAGE_SIZE }
  Object.entries(filters).forEach(([k, v]) => {
    if (v) params[k] = v
  })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['ai-calls', params],
    queryFn: () => aiCallsApi.list(params),
    refetchInterval: 30_000,
  })

  const { data: usage } = useQuery({
    queryKey: ['ai-usage'],
    queryFn: aiCallsApi.usage,
    refetchInterval: 60_000,
  })

  // Balances come from the providers themselves, so they are polled slowly:
  // a credit balance does not move between page loads, and each refresh is a
  // request against somebody else's rate limit.
  const { data: balances } = useQuery({
    queryKey: ['ai-balances'],
    queryFn: aiCallsApi.balances,
    refetchInterval: 300_000,
  })

  const summary = usage?.[period]

  const update = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(1)
  }

  return (
    <div className="ai-calls-page">
      <PageHeader
        title="AI activity"
        subtitle="Which providers are connected, what they have been used for, and what it cost"
      />

      {/* Who is connected, and where their own account lives.

          No figures. Spend belongs to the Usage panel below, which already
          breaks it down by provider and period; repeating one provider's
          total up here invited it to be read as a balance. And the balance
          itself was never comparable between the two — OpenRouter publishes
          one and Sarvam does not — so two cards side by side showed two
          different kinds of number under the same heading. The link is the
          honest answer to 'how much is left': it goes to the account that
          knows. */}
      <section className="ai-balances" aria-busy={!balances}>
        {!balances &&
          [0, 1].map((i) => (
            <div key={i} className="ai-balance">
              <div className="ai-balance-head">
                <Skeleton w="20px" h={20} radius={4} />
                <Skeleton w="110px" h={15} />
              </div>
              <Skeleton w="150px" h={13} />
            </div>
          ))}
        {(balances ?? []).map((b) => (
          <div key={b.provider} className="ai-balance">
            <div className="ai-balance-head">
              <img
                className="ai-balance-logo"
                src={`https://${iconHost(b.dashboard)}/favicon.ico`}
                alt=""
                aria-hidden="true"
                // A provider that serves no favicon leaves the name alone
                // rather than a broken-image box.
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              <span className="ai-balance-name">{b.label}</span>
              {b.connected ? (
                <span className="badge status-confirmed">connected</span>
              ) : (
                <span className="badge status-new">not connected</span>
              )}
            </div>

            {b.dashboard && (
              <a
                className="ai-balance-link"
                href={`https://${b.dashboard}`}
                target="_blank"
                rel="noreferrer"
              >
                {b.dashboard} ↗
              </a>
            )}
          </div>
        ))}
      </section>

      <section className="panel">
        <div className="panel-title panel-title-row">
          <span>Usage</span>
          <span className="ai-periods">
            {Object.entries(PERIODS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`ai-period${period === key ? ' on' : ''}`}
                onClick={() => setPeriod(key)}
              >
                {label}
              </button>
            ))}
          </span>
        </div>

        {!summary && (
          <div className="ai-usage-figures" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <Skeleton w="72px" h={26} />
                <Skeleton w="54px" h={11} radius={4} className="skeleton-gap" />
              </div>
            ))}
          </div>
        )}

        {summary && (
          <>
            <div className="ai-usage-figures">
              <div>
                <div className="ai-usage-value">{summary.calls}</div>
                <div className="ai-usage-label">Calls</div>
              </div>
              {spendByProvider(summary).map((row) => (
                <div key={`${row.provider}-${row.currency}`}>
                  <div className="ai-usage-value">{formatCost(row.cost, row.currency)}</div>
                  <div className="ai-usage-label">{providerName(row.provider)}</div>
                </div>
              ))}
              {!spendByProvider(summary).length && (
                <div>
                  <div className="ai-usage-value">—</div>
                  <div className="ai-usage-label">Spent</div>
                </div>
              )}
              <div>
                <div className={`ai-usage-value${summary.errors ? ' bad' : ''}`}>
                  {summary.errors}
                </div>
                <div className="ai-usage-label">
                  {summary.error_rate != null
                    ? `Failed · ${percent(summary.error_rate)}`
                    : 'Failed'}
                </div>
              </div>
            </div>

            {summary.by_provider.length === 0 ? (
              <p className="hint">Nothing in this period.</p>
            ) : (
              <table className="table compact ai-usage-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Stage</th>
                    <th className="right">Calls</th>
                    <th className="right">Errors</th>
                    <th className="right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_provider.map((row, i) => (
                    <tr key={i}>
                      <td className="strong">{providerName(row.provider)}</td>
                      <td>{OPERATION_LABELS[row.operation] ?? titleCase(row.operation)}</td>
                      <td className="right">{row.calls}</td>
                      <td className="right">{row.errors || '—'}</td>
                      <td className="right">{formatCost(row.cost, row.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        <p className="hint">
          Costs are grouped by currency and never converted — Sarvam bills in ₹,
          OpenRouter in $.
        </p>
      </section>

      <Toolbar>
        <select
          value={filters.operation}
          onChange={(e) => update('operation', e.target.value)}
        >
          <option value="">All stages</option>
          <option value="ocr">Image → text</option>
          <option value="stt">Audio → text</option>
          <option value="extraction">Text → lines</option>
          <option value="translation">Name → Tamil</option>
        </select>

        <select
          value={filters.provider}
          onChange={(e) => update('provider', e.target.value)}
        >
          <option value="">All providers</option>
          <option value="sarvam">Sarvam</option>
          <option value="openrouter">OpenRouter</option>
        </select>

        <select value={filters.status} onChange={(e) => update('status', e.target.value)}>
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
        </select>
      </Toolbar>

      {/* The real table's own head, so the columns do not shift when the
          rows arrive — the whole point of a skeleton over 'Loading…'. */}
      {isLoading && (
        <table className="table" aria-busy="true">
          <thead>
            <tr>
              <th>When</th>
              <th>Stage</th>
              <th>Provider / model</th>
              <th>Result</th>
              <th className="right">Cost</th>
            </tr>
          </thead>
          <tbody>
            <SkeletonRows rows={8} cols={5} />
          </tbody>
        </table>
      )}
      {isError && <ErrorNote message="Could not load the AI log" onRetry={refetch} />}
      {data && !data.items.length && (
        <Empty
          icon="🧾"
          title="No AI calls logged yet"
          hint="Every OCR, transcription and extraction request will appear here."
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Stage</th>
                <th>Provider / model</th>
                <th>Result</th>
                <th className="right">Cost</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((call) => (
                // Key belongs on the Fragment: the row and its expanded
                // detail row are siblings from one iteration.
                <Fragment key={call.id}>
                  <tr className={call.status === 'error' ? 'row-danger' : ''}>
                    <td className="muted">{dateTime(call.created_at)}</td>
                    <td>{OPERATION_LABELS[call.operation] ?? call.operation}</td>
                    <td>
                      <div className="strong">{call.provider}</div>
                      <div className="mono muted">{call.model ?? '—'}</div>
                      {call.mode && <span className="chip-static">{call.mode}</span>}
                    </td>
                    <td className="truncate">
                      {call.status === 'error' ? (
                        <span className="missing">{call.error}</span>
                      ) : (
                        call.output_preview || <span className="muted">(empty)</span>
                      )}
                      {call.source === 'playground' && (
                        <span className="badge warn-sm">test bench</span>
                      )}
                    </td>
                    <td className="right mono">
                      {formatCost(call.cost, call.currency)}
                      {call.billing_units && (
                        <div className="muted">{call.billing_units}</div>
                      )}
                    </td>
                    <td className="right">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setOpenId(openId === call.id ? null : call.id)}
                      >
                        {openId === call.id ? 'Close' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {openId === call.id && (
                    <tr>
                      <td colSpan={7} className="detail-cell">
                        <CallDetail id={call.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  )
}

/** The full stored exchange for one call. */
function CallDetail({ id }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-call', id],
    queryFn: () => aiCallsApi.get(id),
  })

  if (isLoading) return <Loading label="Loading call…" />
  if (isError) return <ErrorNote message="Could not load this call" />

  return (
    <div className="call-detail">
      <dl className="kv detail-kv">
        <dt>Stage</dt>
        <dd>{OPERATION_LABELS[data.operation] ?? data.operation}</dd>

        <dt>Provider</dt>
        <dd>
          {data.provider} · <span className="mono">{data.model ?? '—'}</span>
          {data.mode && <span className="chip-static"> {data.mode}</span>}
        </dd>

        <dt>Outcome</dt>
        <dd>
          {titleCase(data.status)} · {data.latency_ms} ms
          {data.cost != null && ` · ${formatCost(data.cost, data.currency)}`}
          {data.billing_units && <span className="muted"> ({data.billing_units})</span>}
        </dd>

        {data.provider_request_id && (
          <>
            <dt>Provider ref</dt>
            <dd className="mono">{data.provider_request_id}</dd>
          </>
        )}

        {data.input_bytes != null && (
          <>
            <dt>Input</dt>
            <dd>
              {(data.input_bytes / 1024).toFixed(1)} KB · {data.input_mime ?? '—'}
            </dd>
          </>
        )}

        {data.message_id && (
          <>
            <dt>Message</dt>
            <dd>#{data.message_id}</dd>
          </>
        )}

        {data.order_id && (
          <>
            <dt>Order</dt>
            <dd>
              <Link to={`/orders/${data.order_id}`}>View order</Link>
            </dd>
          </>
        )}
      </dl>

      {data.request_params && (
        <>
          <div className="line-label">Request parameters</div>
          <pre className="bench-text small">
            {JSON.stringify(data.request_params, null, 2)}
          </pre>
        </>
      )}

      {data.input_text && (
        <>
          <div className="line-label">Input text</div>
          <pre className="bench-text small">{data.input_text}</pre>
        </>
      )}

      {data.error && (
        <>
          <div className="line-label">Error</div>
          <pre className="bench-text small error-text">{data.error}</pre>
        </>
      )}

      {data.output_text && (
        <>
          <div className="line-label">Output text</div>
          <pre className="bench-text small">{data.output_text}</pre>
        </>
      )}

      {data.raw_response && (
        <>
          <div className="line-label">Raw provider response</div>
          <pre className="bench-text small">
            {JSON.stringify(data.raw_response, null, 2)}
          </pre>
        </>
      )}
    </div>
  )
}
